import {
    useAbility
} from '@casl/react';
import {
    faGear
} from '@fortawesome/pro-light-svg-icons/faGear';
import {
    faRightFromBracket
} from '@fortawesome/pro-light-svg-icons/faRightFromBracket';
import {
    faSearch
} from '@fortawesome/pro-regular-svg-icons/faSearch';
import {
    faUser
} from '@fortawesome/pro-solid-svg-icons/faUser';
import {
    FontAwesomeIcon
} from '@fortawesome/react-fontawesome';
import {
    Box,
    Drawer,
    Grid2,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Button as MUIButton,
    Link as MUILink,
    Popover,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    type FC,
    type MouseEvent,
    type ReactNode,
    memo,
    useContext,
    useEffect,
    useState
} from 'react';
import {
    Link,
    NavLink,
    useLocation,
    useNavigate,
    useSearchParams
} from 'react-router-dom';
import {
    toast
} from 'react-toastify';

import SearchForm from '../SearchForm';

import Button from '@components/typed/Button';

import DialogNotifications from '@components/shared/DialogNotifications';

import {
    canViewManage
} from '@auth/authHelpers';
import {
    AbilityContext
} from '@auth/Can';
import {
    useAppAuth
} from '@auth/useAppAuth';

import isFiniteNumber from '@utils/isFiniteNumber';

import {
    HOME as ROUTE_HOME,
    SEARCH as ROUTE_SEARCH,
    SUPPORTERS as ROUTE_SUPPORTERS
} from '@constants/routes';
import {
    LISTING_TYPE_ORGANIZATION,
    LISTING_TYPE_SPONSOR
} from '@constants/site';
import {
    POL_THEME,
    ThemeContext
} from '@constants/theme';
import {
    Titles
} from '@constants/titles';

import type {
    SICCodeCategory
} from '@types';

import type {
    AppHeaderProps,
    SearchFormToggle
} from './types';

import LogoMCO from '@public/svg/logo-mco.svg';
import LogoPOL from '@public/svg/logo-pol.svg';

import styles from './styles';

const AppHeader: FC<AppHeaderProps> = (props) => {
    const {
        isLandingPage
    } = props;

    const themeContext = useContext(ThemeContext);

    const {
        isAuthenticated,
        loginWithPopup,
        logout,
        user
    } = useAppAuth();

    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [
        searchParams
    ] = useSearchParams();

    const muiTheme = useTheme();
    const isDesktopMdUp = useMediaQuery(
        muiTheme.breakpoints.up('desktopMedium')
    );
    const isMdDown = useMediaQuery(
        muiTheme.breakpoints.down('md')
    );
    const isSmDown = useMediaQuery(
        muiTheme.breakpoints.down('sm')
    );

    const path = location.pathname;

    const isPOL = themeContext === POL_THEME;

    const searchCategory = searchParams.get('searchCategory') ?? undefined;
    const searchLocation = searchParams.get('searchLocation')?.trim() ?? undefined;
    const searchName = searchParams.get('searchName')?.trim() ?? undefined;
    const searchType = searchParams.get('searchType') ?? undefined;
    const isSupporterSearchPage = path === ROUTE_SEARCH && searchType === LISTING_TYPE_SPONSOR;
    const isSearchCategoryValid = searchCategory && isFiniteNumber(Number(searchCategory)) && searchCategory.length < 3;

    const logoFillColor = muiTheme.colors.plantation;
    const logoHeightPOL = isMdDown ? 30 : 58;
    const logoHeightMCO = isMdDown ? 20 : 40;

    const menuId = 'account-popover';

    const [
        searchNameValue,
        setSearchNameValue
    ] = useState<string>(searchName || '');
    const [
        searchLocationValue,
        setSearchLocationValue
    ] = useState<string>(searchLocation || '');
    const [
        selectedSICCategory,
        setSelectedSICCategory
    ] = useState<SICCodeCategory['id'] | ''>(isSearchCategoryValid ? Number(searchCategory) : '');
    const [
        searchFormToggle,
        setSearchFormToggle
    ] = useState<SearchFormToggle>(LISTING_TYPE_ORGANIZATION);
    const [
        anchorEl,
        setAnchorEl
    ] = useState<HTMLButtonElement | null>(null);
    const [
        isMobileDrawerOpen,
        setIsMobileDrawerOpen
    ] = useState<boolean>(false);

    const menuOpen = !!anchorEl;

    const handleMenuClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleLogout = (): void => {
        setAnchorEl(null);

        logout();
    };

    useEffect(() => {
        if (user && !user.isEmailVerified) {
            toast.success('You are signed up successfully', {
                autoClose: 3000
            });

            toast.warning('Please verify your email address to log in to Online Directory', {
                autoClose: 3000,
                onClose: logout
            });
        }
    }, [
        user,
        logout
    ]);

    useEffect(
        (): void => {
            if (!isSupporterSearchPage) {
                setSearchFormToggle(LISTING_TYPE_ORGANIZATION);

                return;
            }

            setSearchFormToggle(LISTING_TYPE_SPONSOR);
        },
        [
            isSupporterSearchPage
        ]
    );

    useEffect(
        (): void => {
            setSearchNameValue(searchName || '');
            setSearchLocationValue(searchLocation || '');

            if (isSearchCategoryValid && isSupporterSearchPage) {
                setSelectedSICCategory(Number(searchCategory));
            }

            if (!isSupporterSearchPage) {
                setSelectedSICCategory('');
            }
        },
        [
            isSearchCategoryValid,
            isSupporterSearchPage,
            searchCategory,
            searchLocation,
            searchName
        ]
    );

    const renderLogo = (
        <Link
            aria-label={isPOL ? Titles.POL_HOME : Titles.MCO_HOME}
            to={ROUTE_HOME}
        >
            {isPOL ? (
                <LogoPOL
                    aria-hidden
                    fill={logoFillColor}
                    height={logoHeightPOL}
                />
            ) : (
                <LogoMCO
                    aria-hidden
                    fill={logoFillColor}
                    height={logoHeightMCO}
                />
            )}
        </Link>
    );

    const searchForm = (
        <SearchForm
            isSupporterSearch={searchFormToggle === LISTING_TYPE_SPONSOR}
            searchLocationValue={searchLocationValue}
            searchNameValue={searchNameValue}
            selectedSICCategory={selectedSICCategory}
            setIsSearchDrawerOpen={setIsMobileDrawerOpen}
            setSearchLocationValue={setSearchLocationValue}
            setSearchNameValue={setSearchNameValue}
            setSelectedSICCategory={setSelectedSICCategory}
        />
    );

    const landingPageLinks = (
        <Box sx={styles.navLinkContainer}>
            <MUILink
                component={NavLink}
                sx={styles.navLink}
                to={ROUTE_HOME}
            >
                {`Search ${isPOL ? 'Churches' : 'Communities'}`}
            </MUILink>
            <MUILink
                component={NavLink}
                sx={styles.navLink}
                to={ROUTE_SUPPORTERS}
            >
                {`Search Businesses${!isSmDown ? ' We Trust' : ''}`}
            </MUILink>
        </Box>
    );

    const searchToggleButtons = (
        <Box sx={styles.navLinkContainer}>
            <MUIButton
                aria-pressed={searchFormToggle === LISTING_TYPE_ORGANIZATION}
                disableRipple
                onClick={() => setSearchFormToggle(LISTING_TYPE_ORGANIZATION)}
                sx={[
                    styles.navLink,
                    searchFormToggle === LISTING_TYPE_ORGANIZATION && styles.navLinkActive
                ]}
                variant={'text'}
            >
                {`Search ${isPOL ? 'Churches' : 'Communities'}`}
            </MUIButton>
            <MUIButton
                aria-pressed={searchFormToggle === LISTING_TYPE_SPONSOR}
                disableRipple
                onClick={() => setSearchFormToggle(LISTING_TYPE_SPONSOR)}
                sx={[
                    styles.navLink,
                    searchFormToggle === LISTING_TYPE_SPONSOR && styles.navLinkActive
                ]}
                variant={'text'}
            >
                {'Search Businesses'}
            </MUIButton>
        </Box>
    );

    const headerLinks = isLandingPage ? landingPageLinks : searchToggleButtons;

    const accountLoginButton = (): ReactNode => {
        if (isAuthenticated) {
            return (
                <Button
                    aria-controls={menuOpen ? menuId : undefined}
                    aria-expanded={menuOpen}
                    aria-label={'Account'}
                    onClick={handleMenuClick}
                    size={'small'}
                    sx={[
                        styles.menuButton,
                        styles.roundButton
                    ]}
                    title={'Account'}
                    variant={'outlined'}
                >
                    <FontAwesomeIcon
                        aria-hidden
                        icon={faUser}
                        size={'lg'}
                    />
                </Button>
            );
        }

        return (
            <Button
                onClick={loginWithPopup}
                size={'small'}
                sx={styles.menuButton}
                type={'button'}
                typographyVariant={'caption'}
                variant={'outlined'}
            >
                {'Log In'}
            </Button>
        );
    };

    const desktopHeader = (
        <Box
            aria-label={'Primary Navigation'}
            component={'nav'}
            sx={[
                styles.headerContainer,
                isLandingPage && styles.landingPageHeader
            ]}
        >
            {renderLogo}

            {!isLandingPage && searchForm}

            {headerLinks}

            {accountLoginButton()}
        </Box>
    );

    const tabletHeader = (
        <Box
            aria-label={'Primary Navigation'}
            component={'nav'}
        >
            <Box
                sx={[
                    styles.headerContainer,
                    isLandingPage && styles.landingPageHeader
                ]}
            >
                {renderLogo}

                {headerLinks}

                {accountLoginButton()}
            </Box>
            {!isLandingPage && (
                <Box
                    sx={[
                        styles.navBar,
                        styles.tabletNav
                    ]}
                >
                    {searchForm}
                </Box>
            )}
        </Box>
    );

    const mobileHeaderContainer = (
        <Box
            sx={[
                styles.headerContainer,
                isLandingPage && styles.landingPageHeader
            ]}
        >
            <Box>
                {renderLogo}
            </Box>

            <Grid2
                container
                spacing={{
                    mobileMedium: 1.5,
                    xs: 0.75
                }}
            >
                {!isLandingPage && (
                    <Button
                        aria-label={'Toggle Search Form'}
                        onClick={() => setIsMobileDrawerOpen((prev) => !prev)}
                        sx={[
                            styles.roundButton,
                            styles.roundSearchButton
                        ]}
                        title={'Toggle Search Form'}
                        type={'button'}
                    >
                        <FontAwesomeIcon
                            aria-hidden
                            icon={faSearch}
                            size={'xl'}
                        />
                    </Button>
                )}

                {accountLoginButton()}
            </Grid2>
        </Box>
    );

    const mobileHeader = (
        <>
            <Box
                aria-label={'Primary Navigation'}
                component={'nav'}
            >
                {mobileHeaderContainer}
            </Box>

            {isLandingPage ? (
                <Box sx={styles.navBar}>
                    <Box sx={styles.mobileNav}>
                        {headerLinks}
                    </Box>
                </Box>
            ) : (
                <Drawer
                    anchor={'top'}
                    ModalProps={{
                        disableScrollLock: true,
                        keepMounted: true
                    }}
                    onClose={() => setIsMobileDrawerOpen(false)}
                    open={isMobileDrawerOpen}
                    slotProps={{
                        paper: {
                            'aria-label': 'Mobile Menu',
                            'aria-modal': true,
                            role: 'dialog'
                        }
                    }}
                    variant={'temporary'}
                >
                    {isMobileDrawerOpen && <DialogNotifications />}
                    <Box
                        aria-label={'Mobile Navigation'}
                        component={'nav'}
                    >
                        {mobileHeaderContainer}

                        <Box sx={styles.navBar}>
                            <Box sx={styles.mobileNav}>
                                {headerLinks}
                            </Box>

                            <Box sx={styles.mobileNav}>
                                {searchForm}
                            </Box>
                        </Box>
                    </Box>
                </Drawer>
            )}
        </>
    );

    const defaultHeader = (): ReactNode => {
        if (isDesktopMdUp) {
            return desktopHeader;
        }

        if (!isMdDown) {
            return tabletHeader;
        }

        return mobileHeader;
    };

    const landingPageHeader = () => {
        if (!isMdDown) {
            return desktopHeader;
        }

        return mobileHeader;
    };

    return (
        <>
            <Box
                component={'header'}
                sx={styles.container}
            >
                {isLandingPage ? landingPageHeader() : defaultHeader()}
            </Box>

            <Popover
                anchorEl={anchorEl}
                anchorOrigin={{
                    horizontal: 'left',
                    vertical: 'bottom'
                }}
                aria-label={'Account options'}
                id={menuId}
                onClose={() => setAnchorEl(null)}
                open={menuOpen}
                sx={styles.popover}
            >
                <List>
                    {isAuthenticated && (
                        <>
                            {canViewManage(ability) && (
                                <>
                                    <ListItem
                                        disablePadding

                                    >
                                        <ListItemButton
                                            onClick={() => {
                                                setAnchorEl(null);
                                                navigate('/admin/settings');
                                            }}
                                        >
                                            <ListItemIcon>
                                                <FontAwesomeIcon
                                                    aria-hidden
                                                    icon={faGear}
                                                />
                                            </ListItemIcon>
                                            <ListItemText primary={'Manage'} />
                                        </ListItemButton>
                                    </ListItem>
                                    <ListItem sx={styles.menuDivider} />
                                </>
                            )}

                            <ListItem
                                disablePadding

                            >
                                <ListItemButton onClick={handleLogout}>
                                    <ListItemIcon>
                                        <FontAwesomeIcon
                                            aria-hidden
                                            icon={faRightFromBracket}
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={'Log Out'} />
                                </ListItemButton>
                            </ListItem>
                        </>
                    )}
                </List>
            </Popover>
        </>
    );
};

export default memo(AppHeader);
